import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { IInvoice, ISupplierStatement, StockReceived } from '$models'
import { invoiceService, supplierStatementService } from '$services'
import { InvoiceSlice } from '../../../invoices/edit/slice'
import { SupplierStatementsSlice } from '../slice'
import { addStockReceivedOrdered } from '../_common/helpers/helpers'

interface EditSupplierStatementState {
  supplierStatement: ISupplierStatement | null
  isSavingAdjustingInvoice: boolean
  isDeletingAdjustingInvoice: boolean
}

export interface EditSupplierStatementSlice extends EditSupplierStatementState {
  resetEditSupplierStatementSlice: () => void

  getSupplierStatement: (id: string) => Promise<ISupplierStatement | null>

  updateSupplierStatementWithStockReceivedReconciled(
    documentId: string,
    reconciled: boolean
  ): void

  setSupplierStatement(supplierStatement: ISupplierStatement): void

  updateSupplierStatement: (
    supplierStatement: ISupplierStatement,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<ISupplierStatement | null>

  addAdjustingInvoice: (
    adjustingInvoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  setIsSavingAdjustingInvoice: (isSavingAdjustingInvoice: boolean) => void

  deleteAdjustingInvoice: (
    stockReceivedId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  setIsDeletingAdjustingInvoice: (isDeletingAdjustingInvoice: boolean) => void
}

const initialState: EditSupplierStatementState = {
  supplierStatement: null,
  isSavingAdjustingInvoice: false,
  isDeletingAdjustingInvoice: false,
}

const createEditSupplierStatementSlice: StateCreator<
  EditSupplierStatementSlice &
    SupplierStatementsSlice &
    InvoiceSlice &
    SharedSlice,
  [],
  [],
  EditSupplierStatementSlice
> = (set, get) => ({
  ...initialState,

  resetEditSupplierStatementSlice: () => set(initialState),

  setIsSavingAdjustingInvoice: (isSavingAdjustingInvoice: boolean): void => {
    set(() => ({
      isSavingAdjustingInvoice: isSavingAdjustingInvoice,
    }))
  },

  setIsDeletingAdjustingInvoice: (
    isDeletingAdjustingInvoice: boolean
  ): void => {
    set(() => ({
      isDeletingAdjustingInvoice: isDeletingAdjustingInvoice,
    }))
  },

  getSupplierStatement: async (
    id: string
  ): Promise<ISupplierStatement | null> => {
    try {
      const supplierStatement: ISupplierStatement =
        await supplierStatementService.getSupplierStatement(id)

      if (
        !supplierStatement.startAt ||
        !supplierStatement.endAt ||
        !supplierStatement.supplierId
      )
        return supplierStatement

      const periodStart = supplierStatement.startAt
      const periodEnd = supplierStatement.endAt.add(1, 'day') //End dates are exclusive in our API, but inclusive on the statement

      await get().getStockReceivedRows(
        periodStart,
        periodEnd,
        supplierStatement.supplierId,
        true,
        supplierStatement.id
      )

      set(() => ({
        supplierStatement: supplierStatement,
      }))

      return supplierStatement
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve supplier statement')
      return null
    }
  },

  updateSupplierStatementWithStockReceivedReconciled(
    documentId: string,
    reconciled: boolean
  ): void {
    const updatedStockReceivedItems = get().updateStockReceivedReconciled(
      documentId,
      reconciled
    )

    set((state) => ({
      supplierStatement: {
        ...state.supplierStatement,
        reconciledStockReceivedItems: updatedStockReceivedItems
          .filter(
            (stockReceive) => stockReceive.reconciled && stockReceive.isEditable
          )
          .map((stockReceived) => {
            return {
              ...stockReceived,
              statementId: state.supplierStatement!.id,
            } as StockReceived
          }),
      },
    }))
  },

  updateSupplierStatement: async (
    supplierStatement: ISupplierStatement,
    successMessage?: string,
    errorMessage?: string
  ): Promise<ISupplierStatement | null> => {
    try {
      set(() => ({ isLoading: true }))

      const updatedSupplierStatement: ISupplierStatement =
        await supplierStatementService.updateSupplierStatement(
          supplierStatement.id!,
          supplierStatement
        )

      set(() => ({
        supplierStatement: updatedSupplierStatement,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      return updatedSupplierStatement
    } catch (error) {
      exceptionMessageHandler(
        error,
        errorMessage ?? 'Failed to retrieve supplier statement'
      )
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  setSupplierStatement(supplierStatement: ISupplierStatement): void {
    set(() => ({
      supplierStatement: supplierStatement,
    }))
  },

  addAdjustingInvoice: async (
    adjustingInvoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ): Promise<void> => {
    try {
      const createdAdjustingInvoice = await invoiceService.addInvoice(
        adjustingInvoice
      )

      const stockReceivedRows = [
        ...addStockReceivedOrdered(
          get().stockReceivedRows,
          new StockReceived({
            ...createdAdjustingInvoice,
            supplierId: createdAdjustingInvoice.linkedSupplierId,
            invoiceNumber: createdAdjustingInvoice.referenceNumber,
            creditRequest: false,
            invoicedAt: createdAdjustingInvoice.issuedAt,
            reconciled: createdAdjustingInvoice.reconciled,
          })
        ),
      ]

      set(() => ({
        stockReceivedRows: stockReceivedRows,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(
        error,
        errorMessage ?? 'Failed to add Adjusting Invoice'
      )
    }
  },

  deleteAdjustingInvoice: async (
    stockReceivedId: string,
    successMessage?: string,
    errorMessage?: string
  ): Promise<void> => {
    try {
      const isRemoved = await get().removeInvoice(stockReceivedId)

      if (!isRemoved) {
        return
      }

      const stockReceivedItems = get().stockReceivedRows.filter(
        (stockReceive) => stockReceive.id !== stockReceivedId
      )

      set(() => ({
        stockReceivedRows: stockReceivedItems,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(
        error,
        errorMessage ?? 'Failed to delete Adjusting Invoice'
      )
    }
  },
})

export default createEditSupplierStatementSlice
